import { CreateAttempt } from '@/common/types/session';
import { ControllerType } from '@/common/types/student';
import {
  GameInstance,
  getSession,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import { DEFAULT_SCALE_DOWN_CONFIG } from '@/game/GameComponents/PhaserGame/Types/ManagerTypes/ResponsivenessManagerTypes';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import { ScrollGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/ScrollGroup';
import { VerticalLayoutGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/VerticalLayoutGroup';
import {
  countUp,
  getAnchorPlugin,
  getFinalRubiesFromAttemptsAndAccuracy,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import DefaultButton from '@/game/prefabs/GeneralUI/Buttons/DefaultButton';
import MinigameScoreScreen from '@/game/prefabs/Minigame/MinigameScoreScreen';
import StudentGameAttemptUI from '@/game/prefabs/StudentGame/StudentGameAttemptUI/StudentGameAttemptUI';
import Minigame from '@/game/scenes/Minigame';

export default class MinigameScoreScreenController {
  constructor(private scoreScreen: MinigameScoreScreen) {}

  resultsList?: VerticalLayoutGroup;
  resultsList_m?: VerticalLayoutGroup;
  attemptsScrollGroup?: ScrollGroup;
  attemptsScrollGroup_m?: ScrollGroup;

  getResponsiveElements(key: keyof MinigameScoreScreen) {
    return ResponsivenessManager.GetAllResponsiveComponentsFromContainer(
      this.scoreScreen,
      key,
    );
  }

  initialize() {
    const { scoreScreen } = this;
    scoreScreen.speedBonusTooltip.setVisible(false);

    // Desktop results list.
    this.resultsList = new VerticalLayoutGroup({
      parent: scoreScreen.attemptsList,
      elementDist: 8,
      origin: new Phaser.Math.Vector2(0, 0),
      align: 'center',
    });

    // Mobile results list.
    this.resultsList_m = new VerticalLayoutGroup({
      parent: scoreScreen.attemptsList_m,
      elementDist: 4,
      origin: new Phaser.Math.Vector2(0, 0),
      align: 'center',
    });

    // Desktop scroll group.
    this.attemptsScrollGroup = new ScrollGroup({
      scene: scoreScreen.scene,
      content: scoreScreen.attemptsList,
      scrollElement: scoreScreen.attemptsBacking,
      scrollBacking: scoreScreen.scrollBarBacking,
      scrollHandle: scoreScreen.scrollBarHandle,
      scrollSpeedX: 0,
      maskMargins: {
        top: 0,
        bottom: 13,
      },
    });

    // Mobile scroll group.
    this.attemptsScrollGroup_m = new ScrollGroup({
      scene: scoreScreen.scene,
      content: scoreScreen.attemptsList_m,
      scrollElement: scoreScreen.attemptsBacking_m,
      scrollBacking: scoreScreen.scrollBarBacking_m,
      scrollHandle: scoreScreen.scrollBarHandle_m,
      scrollSpeedX: 0,
      maskMargins: {
        top: 0,
        bottom: 22,
      },
    });

    this.initializeResponsiveness();
  }

  showScoreScreen(minigame: Minigame) {
    const { scoreScreen } = this;
    scoreScreen.setVisible(true);
    const currentActivity = getSession().currentActivity;
    if (!currentActivity) {
      console.error('No current activity found');
      return;
    }

    // Create fake attempts. Only for testing in the dev environment.
    const createFakeAttempts = false;
    if (createFakeAttempts && process.env.NODE_ENV === 'development') {
      const fakeAttempts: CreateAttempt[] = [
        {
          organisationId: '',
          studentId: getStudent()?.id ?? '',
          activityId: '',
          activityName: '',
          correctAnswerId: 'Mat',
          control: ControllerType.CLICK,
          correctAnswer: 'Mat',
          studentAnswer: 'Mat',
          startedAt: Date.now(),
          endedAt: Date.now(),
        },
        {
          organisationId: '',
          studentId: getStudent()?.id ?? '',
          activityId: '',
          activityName: '',
          correctAnswerId: 'asatdsa',
          control: ControllerType.CLICK,
          correctAnswer: 'dsadatdsadas',
          studentAnswer: 'Tam',
          startedAt: Date.now(),
          endedAt: Date.now(),
        },
        {
          organisationId: '',
          studentId: getStudent()?.id ?? '',
          activityId: '',
          activityName: '',
          correctAnswerId: 'Mat',
          control: ControllerType.CLICK,
          correctAnswer: 'Mat',
          studentAnswer: 'ttttsdadmmmmmmmmmmmmmmmm',
          startedAt: Date.now(),
          endedAt: Date.now(),
        },
      ];
      fakeAttempts.forEach((attempt) => {
        GameInstance.instance.addAttempt(attempt);
      });
    }

    this.setupForGame(currentActivity.activityName);

    const startTime = getSession().currentActivity?.startedAt ?? 0;
    const endTime = Date.now();

    const startTimeSeconds = new Date(startTime).getTime() / 1000;
    const endTimeSeconds = new Date(endTime).getTime() / 1000;

    const timeTaken = endTimeSeconds - startTimeSeconds;

    const coins = getFinalRubiesFromAttemptsAndAccuracy(
      getSession().attempts,
      currentActivity.accuracy,
    );

    // Do coin countup.
    const countUpDuration = 1500;
    this.getResponsiveElements('rubiesText').forEach(
      (rubiesText: Phaser.GameObjects.BitmapText) => {
        rubiesText.text = `+${coins}`;
        countUp(
          rubiesText,
          coins,
          countUpDuration,
          () => {
            scoreScreen.scene.tweens.add({
              targets: rubiesText,
              scale: 1.2,
              duration: 400,
              ease: 'Power2',
              yoyo: true,
            });
            rubiesText.text = `+${coins}`;
          },
          '+',
        );
      },
    );

    this.getResponsiveElements('accuracyValue').forEach(
      (accuracyValue: Phaser.GameObjects.BitmapText) => {
        accuracyValue.text = `${Math.round(currentActivity.accuracy)}%`;
      },
    );

    // Format time in a human-readable way
    let timeDisplay: string;
    if (timeTaken < 60) {
      timeDisplay = `${Math.round(timeTaken)}s`;
    } else {
      const minutes = Math.floor(timeTaken / 60);
      const seconds = Math.round(timeTaken % 60);
      timeDisplay = `${minutes}m ${seconds}s`;
    }
    this.getResponsiveElements('timeTakenValue').forEach(
      (timeTakenValue: Phaser.GameObjects.BitmapText) => {
        timeTakenValue.text = timeDisplay;
      },
    );

    // Create the results list.
    if (this.resultsList) {
      this.resultsList.clear();
      for (const attempt of [...getSession().attempts]) {
        const attemptUI = new StudentGameAttemptUI(scoreScreen.scene, 0, 0);
        attemptUI.controller.onSpeedBonusIconHoverOn = (
          icon: Phaser.GameObjects.Image,
        ) => {
          scoreScreen.speedBonusTooltip.setVisible(true);
          const iconPos = icon.getWorldTransformMatrix();
          const thisPos = scoreScreen.getWorldTransformMatrix();
          const offset = new Phaser.Math.Vector2(32, -16);
          scoreScreen.speedBonusTooltip.setPosition(
            iconPos.tx - thisPos.tx + offset.x,
            iconPos.ty - thisPos.ty + offset.y,
          );
        };
        attemptUI.controller.onSpeedBonusIconHoverOff = () => {
          scoreScreen.speedBonusTooltip.setVisible(false);
        };
        attemptUI.controller.setAttempt(attempt);
        this.resultsList.addElement(attemptUI);
        this.attemptsScrollGroup?.updateContentParameters();
      }
    }

    // Create the mobile results list.
    if (this.resultsList_m) {
      this.resultsList_m.clear();
      for (const attempt of [...getSession().attempts]) {
        // The student game attempt UI will handle its own responsiveness, so we can reuse it for both desktop and mobile.
        const attemptUI = new StudentGameAttemptUI(scoreScreen.scene, 0, 0);
        attemptUI.controller.onSpeedBonusIconHoverOn = (
          icon: Phaser.GameObjects.Image,
        ) => {
          scoreScreen.speedBonusTooltip.setVisible(true);
          const iconPos = icon.getWorldTransformMatrix();
          const thisPos = scoreScreen.getWorldTransformMatrix();
          const offset = new Phaser.Math.Vector2(32, -16);
          scoreScreen.speedBonusTooltip.setPosition(
            iconPos.tx - thisPos.tx + offset.x,
            iconPos.ty - thisPos.ty + offset.y,
          );
        };
        attemptUI.controller.onSpeedBonusIconHoverOff = () => {
          scoreScreen.speedBonusTooltip.setVisible(false);
        };
        attemptUI.controller.setAttempt(attempt);
        this.resultsList_m.addElement(attemptUI);
        this.attemptsScrollGroup_m?.updateContentParameters();
      }
    }

    this.getResponsiveElements('finishGameButton').forEach(
      (finishGameButton: DefaultButton) => {
        finishGameButton.controller.setSubmissionCallback(() => {
          // Quit the game.
          minigame.quitMinigame(true);
        });
        finishGameButton.controller.makeInteractive(false);
        scoreScreen.scene.tweens.add({
          targets: finishGameButton,
          duration: countUpDuration + 1000,
          scale: finishGameButton.scale,
          onComplete: () => {
            finishGameButton.controller.makeInteractive(true);
          },
        });
      },
    );

    PhaserAudioManager.playSFX(scoreScreen.scene, 'minigame-complete');
  }

  setupForGame(game: string) {
    switch (game) {
      default:
        this.setupForDefaultGame();
        break;
      // case 'sentenceBuilder':
      //   this.setupForSentenceBuilder();
      //   break;
    }
  }

  setupForDefaultGame() {
    const { scoreScreen } = this;
    scoreScreen.yourAnswerHeader.setVisible(true);
    scoreScreen.correctAnswerHeader.setText('Correct Answer');
  }

  setupForSentenceBuilder() {
    const { scoreScreen } = this;
    scoreScreen.yourAnswerHeader.setVisible(false);
    scoreScreen.correctAnswerHeader.setText('Your Answer');
  }

  initializeResponsiveness() {
    const { scoreScreen } = this;

    ResponsivenessManager.InitializeResponsiveBreakpoints({
      scene: scoreScreen.scene,
      desktopView: scoreScreen.desktopView,
      mobileView: scoreScreen.mobileView,
    });

    const anchor = getAnchorPlugin(scoreScreen.scene);
    anchor.add(scoreScreen.mobileView, {
      centerX: '50%',
      centerY: '50%',
    });
    anchor.add(scoreScreen.desktopView, {
      centerX: '50%',
      centerY: '50%',
    });

    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      scoreScreen.desktopView,
      DEFAULT_SCALE_DOWN_CONFIG,
    );
  }
}
